import CellRenderer from './CellRenderer';
import { IResponseData, IColumnList } from '../../types/spreadsheet-types';
import { IExtractionBatch } from '../../types/my-types';
import { IValidationErrorByExtraction } from '../../types/validation-types';
import { ColDef, ValueGetterParams, CellClassRules, CellClassParams } from 'ag-grid-community';
import { featureFlags } from '../../utils/featureFlags';
import { AgGridReact } from 'ag-grid-react';
import { getCellStyles } from '../../utils/validationUtils';
import { VALIDATING_TOOLTIP, VALIDATION_NOT_READY_TOOLTIP } from '../hooks/useValidationFetchState';
import { GRID_FILTER_PARAMS } from './config';

interface RowType {
  extractionId: string;
  validating?: boolean;
  validationNotReady?: boolean;
  [key: string]: any;
}

// FIXME: Fix Types
export const confColumns = (
  retData: IResponseData[],
  eid: string,
  gridRef: AgGridReact,
  extractionIndex: number,
  validationErrorsByExtraction: IValidationErrorByExtraction[],
  extractionBatch: IExtractionBatch,
  focusedCell: { rowId: string; colId: string } | null,
  showValidations: boolean,
  accessibility: boolean,
  dpid: string
) => {
  const cellRendererParams = {
    gridRef,
    extractionIndex,
    eid,
    validationErrorsByExtraction,
    extractionBatch,
    dpid,
  };

  let columns: any = [
    {
      field: 'documentNumber',
      suppressMovable: true,
      headerName: '#',
      width: 45,
      editable: false,
      filter: false,
      cellRenderer: CellRenderer,
      cellRendererParams: { ...cellRendererParams, showRowValidationBadge: true },
      pinned: 'left',
      lockPinned: true,
    },
    {
      field: 'transformationStatus',
      suppressMovable: true,
      editable: false,
      width: 105,
      cellRenderer: CellRenderer,
      cellRendererParams,
      cellClassRules: {
        'processing-failed': (params: CellClassParams<RowType>) => {
          return params.colDef.field === 'transformationStatus' && params.value === 'Failed';
        },
      } as CellClassRules,
    },
    {
      field: 'documentId',
      suppressMovable: true,
      editable: false,
      width: 105,
      cellRenderer: CellRenderer,
      cellRendererParams,
    },
  ];

  // Loop through all extractions and get all possible columns in this batch
  const allColumns: string[] = [];
  retData.forEach((extraction: any) => {
    extraction?.data?.fields?.map((field: any) => {
      if (!allColumns.includes(field.label)) {
        allColumns.push(field.label);
      }
    });

    extraction?.data?.additional_fields.map((field: any) => {
      if (!allColumns.includes(field)) {
        allColumns.push(field);
      }
    });
  });

  let modifiedColumns = allColumns;

  let fieldNamesToHighlight: string[] = [];
  let tagNamesToHighlight: string[] = [];
  let fieldNamesThatMatchTags: string[] = [];
  let allHighlightedFieldNames: string[] = [];

  // IF tags are set on the batch, let's filter through them and find out which fields and fields with
  // tags what we need to highlight.
  if (extractionBatch?.tags?.length > 0 && featureFlags.modifyFieldOrderFromEBTags) {
    // Build an array of field and tag names
    extractionBatch?.tags?.forEach((tag) => {
      if (tag.startsWith('fieldPriority:tag:')) {
        const tagName = tag.replace('fieldPriority:tag:', '');
        tagNamesToHighlight.push(tagName);
      } else if (tag.startsWith('fieldPriority:name:')) {
        const fieldName = tag.replace('fieldPriority:name:', '');
        fieldNamesToHighlight.push(fieldName);
      }
    });

    // Build a list of field names across all extractions that contain a matching tag

    retData.forEach((extraction: any) => {
      extraction?.data?.fields.forEach((field: any) => {
        const hasCommonTag = field.glynt_tags.some((tag: string) =>
          tagNamesToHighlight.includes(tag)
        );
        if (hasCommonTag && !fieldNamesThatMatchTags.includes(field.label)) {
          fieldNamesThatMatchTags.push(field.label);
        }
      });
    });

    allHighlightedFieldNames = [...fieldNamesToHighlight, ...fieldNamesThatMatchTags];

    let displayedFields: string[] = [];

    // Build final order of columns, including highlighted fields at the beginning in the same order
    // they wer originall in
    allColumns.forEach((field) => {
      if (allHighlightedFieldNames.includes(field)) {
        displayedFields.push(field);
      }
    });
    allColumns.forEach((field) => {
      if (!allHighlightedFieldNames.includes(field)) {
        displayedFields.push(field);
      }
    });

    modifiedColumns = displayedFields;
  }

  modifiedColumns.forEach((column: string) => {
    columns.push({
      field: column,
      headerName: column,
      suppressMovable: true,
      sortable: false,
      cellRenderer: CellRenderer,
      cellRendererParams,
      ...(allHighlightedFieldNames.includes(column) && { headerClass: 'priority-column' }),
      tooltipValueGetter: (p: ValueGetterParams<RowType>): string | null => {
        if (!showValidations) return null;
        if (p.data?.validating) return VALIDATING_TOOLTIP;
        if (p.data?.validationNotReady) return VALIDATION_NOT_READY_TOOLTIP;
        const meta = p.data?.fieldMeta?.[p.colDef.field as keyof RowType];
        if (!meta?.validationStatus) return null;
        if (meta.ignored) {
          const reason = meta.dismissalReason ? ` (Reason: ${meta.dismissalReason})` : '';
          return `Error Ignored - ${meta.tooltip}${reason}`.trim();
        }
        return meta.tooltip;
      },
      cellStyle: (params: CellClassParams<RowType>) => {
        const fieldMeta = params.data?.fieldMeta?.[params.colDef.field as keyof RowType];
        const isHighlighted =
          params.data?.extractionId === focusedCell?.rowId &&
          params.colDef.field === focusedCell?.colId;

        return getCellStyles({
          accessibility,
          validationStatus: showValidations ? fieldMeta?.validationStatus || null : null,
          isIgnored: showValidations && !!fieldMeta?.ignored,
          isHighlighted,
        });
      },
      filter: 'agSetColumnFilter',
      filterParams: GRID_FILTER_PARAMS,
    });
  });

  return columns;
};
